import React from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import CardSelect from '../../../common/CardSelect';
import messages from './messages';
import { priceToString } from '../../../../utils';
import { getCookie } from '../../../../utils/common/common';
import styles from './ShippingMethodForProducts.module.scss';
import { TOOLTIP_STYLES } from '../../../common/Tooltip/constants';
import Sla from '../../../Sla';

import {
  DEFAULT_IN_HOME_SETUP_PRICE_CENT,
  FEEL_VARIANT_NAME_COOKIE,
  FIRM_VARIANT_TITLE,
  IN_HOME_SETUP_FREE_SHIPPING_METHOD_KEY,
  SOFT_VARIANT_TITLE,
} from '../../../../utils/common/constants';
import { selectIsOverwritePaidInHomeShippingABTest } from '../../../../store/selectors/cart';
import { ShippingMethodForProductsProps } from './types';
import Tooltip from '../../../common/Tooltip';

const STANDARD_SHIPPING_KEY = 'FEDEX_GROUND';
const IN_HOME_SETUP_KEY = 'in-home-setup-qualified';

const ShippingMethodForProducts = ({
  availableShippingMethods,
  groupId,
  parentKey,
  products,
  selectedShippingMethod,
  onSelectShippingMethod,
  slaMessage = null,
}: ShippingMethodForProductsProps) => {
  const { formatNumber, formatMessage } = useIntl();

  const hideFeelCookie = getCookie(FEEL_VARIANT_NAME_COOKIE);
  const hideFeelVariantName =
    hideFeelCookie?.value === 'hide_feel_variant_test';

  const findFeelVariantName = (title: string) =>
    title.includes(SOFT_VARIANT_TITLE) || title.includes(FIRM_VARIANT_TITLE);

  const isOverwritePaidInHomeShippingABTest = useSelector(
    selectIsOverwritePaidInHomeShippingABTest,
  );

  return (
    <div className={styles['shipping-methods-wrapper']}>
      {products.map(({ name, quantity, image, title }, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className={styles['product-wrapper']} key={`${name}-${i}`}>
          <img src={image.url} alt={name} />
          <div className={styles['product-info-container']}>
            <div className={styles['product-name']}>{name}</div>
            <div className={styles['product-title']}>
              {findFeelVariantName(title) && hideFeelVariantName
                ? title.slice(6)
                : title}
            </div>
            <div className={styles['quantity-label']}>
              {formatMessage(messages.productQuantityLabel, { quantity })}
            </div>
          </div>
        </div>
      ))}
      <span className={styles['delivery-methods-options-wrapper']}>
        {availableShippingMethods.map(
          ({ key, name, price, description, offerRemovalOption }) => (
            <CardSelect
              value={`${key}`}
              id={`${parentKey}-${key}-${groupId}`}
              key={`${parentKey}-${key}-${groupId}`}
              name={`${parentKey}-${groupId}`}
              isSelected={`${key}` === `${selectedShippingMethod?.key}`}
              onChange={() => {
                onSelectShippingMethod({
                  parentKey,
                  groupId,
                  key: key ?? '',
                  name: name ?? '',
                  cost: price?.centAmount ?? 0,
                  needsRemoval: !!offerRemovalOption,
                });
              }}
              title={
                <div className={styles['title-wrapper']}>
                  <div>{name}</div>
                  <div className={styles['price-section']}>
                    {isOverwritePaidInHomeShippingABTest &&
                    key === IN_HOME_SETUP_FREE_SHIPPING_METHOD_KEY ? (
                      <div className={styles['price-off']}>
                        {priceToString({
                          price: {
                            centAmount:
                              getCookie(
                                process.env
                                  .CUSTOM_IN_HOME_SETUP_PRICING_AB_TEST_COOKIE_NAME,
                              )?.value ?? DEFAULT_IN_HOME_SETUP_PRICE_CENT,
                            fractionDigits: 2,
                            currencyCode: 'USD',
                          },
                          maximumFractionDigits: 2,
                          formatNumber,
                          formatMessage,
                        })}
                      </div>
                    ) : null}
                    <div
                      className={classnames({
                        [styles['is-free']]: !price?.centAmount,
                        [styles['free-highlight']]:
                          isOverwritePaidInHomeShippingABTest &&
                          key === IN_HOME_SETUP_FREE_SHIPPING_METHOD_KEY,
                      })}
                    >
                      {priceToString({
                        price,
                        formatNumber,
                        formatMessage,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  </div>
                </div>
              }
            >
              <div className="text-formatted">
                <div className={styles['shipping-method-description']}>
                  <span className={styles['shipping-method-description-sla']}>
                    <Sla
                      slaMessage={slaMessage}
                      isDeliveryStep
                      deliveryType={key}
                      inline
                    />
                  </span>
                  {description}
                  {parentKey === IN_HOME_SETUP_KEY &&
                    key === STANDARD_SHIPPING_KEY &&
                    formatMessage(messages.loeMsg)}
                </div>
                {!!offerRemovalOption && (
                  <div
                    className={
                      styles['free-mattress-removal-included-container']
                    }
                  >
                    <div
                      className={styles['free-mattress-removal-included-label']}
                    >
                      {formatMessage(messages.freeMattressRemovalIncluded)}
                    </div>
                    <div>
                      <Tooltip
                        delay={0}
                        direction="right-edge"
                        align="top"
                        content=""
                        style={TOOLTIP_STYLES.square}
                        reactNodesContent={
                          <div
                            className={
                              styles['shipping-methods-tooltip-content']
                            }
                          >
                            <div>
                              {formatMessage(
                                messages.mattressRemovalTooltipParagraph1,
                              )}
                            </div>
                            <div>
                              {formatMessage(
                                messages.mattressRemovalTooltipParagraph2,
                              )}
                            </div>
                          </div>
                        }
                      >
                        <div
                          className={styles['info-icon']}
                          onClick={(e) => e.preventDefault()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                            }
                          }}
                          role="button"
                          aria-label="info-icon"
                          tabIndex={0}
                        />
                      </Tooltip>
                    </div>
                  </div>
                )}
              </div>
            </CardSelect>
          ),
        )}
      </span>
    </div>
  );
};

export default ShippingMethodForProducts;
